import { appShell } from './app';

/**
 * Something the Mac shell hands the page to send on the spot (lib/quickSend.ts): from the global shortcut or from the
 * Share menu (apps/apple/ShareExtension). The shell leaves a note on `window.bridgeApp.quickSend` with the text or
 * link, and a file it serves once, at `/_share/<id>/file` on the app's own origin.
 */

/** What the shell handed over: text or a link, a file, or both. */
export interface SharedContent {
  readonly text: string | null;
  readonly file: File | null;
}

const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** True for the lower-case UUIDs the shell uses as ids. */
export function isShareId(value: unknown): value is string {
  return typeof value === 'string' && ID.test(value);
}

interface Note {
  readonly text: string | null;
  readonly id: string | null;
  readonly name: string;
  readonly type: string;
}

function readNote(): Note | null {
  if (appShell() === null) return null;
  const note = (window.bridgeApp as Record<string, unknown>).quickSend;
  if (typeof note !== 'object' || note === null) return null;
  const { text, id, name, type } = note as Record<string, unknown>;
  const result: Note = {
    text: typeof text === 'string' && text.trim() !== '' ? text : null,
    id: typeof id === 'string' && ID.test(id) ? id : null,
    name: typeof name === 'string' && name !== '' ? name : 'file',
    type: typeof type === 'string' ? type : '',
  };
  return result.text === null && result.id === null ? null : result;
}

/**
 * Takes what the shell left, once: the note is cleared before the file is read, so a second call (a remounted
 * screen, a second event) finds nothing. Null in a browser and when nothing was shared. A file that cannot be read
 * is dropped and the text, if any, still arrives.
 */
export async function takeAppShare(): Promise<SharedContent | null> {
  const note = readNote();
  if (note === null) return null;
  Reflect.deleteProperty(window.bridgeApp as Record<string, unknown>, 'quickSend');
  let file: File | null = null;
  if (note.id !== null) {
    try {
      const response = await fetch(`/_share/${note.id}/file`);
      if (response.ok) file = new File([await response.arrayBuffer()], note.name, { type: note.type });
    } catch {
      // the shell served the file already, or is gone: the person shares again
    }
  }
  return note.text === null && file === null ? null : { text: note.text, file };
}
